import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { Button } from '@sunrun/component-library';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { Color } from '@sunrun/mui-theme';
import mixpanel from '../../utilities/mixpanel';
import { useContactId, useProspectId } from '../../hooks/useUser';
import { createFeedback } from '../../redux/modules/feedback';
import { MAX_DESKTOP_BREAKPOINT } from '../general/constants/_breakpoints';

import Form from '../feedback/Form';
import { SECONDARY_COLOR } from '../general/constants/_colors';

const UserAnswers = Object.freeze({
  YES: 'Yes',
  NO: 'No',
});

const UserFeedbackComponentContainer = styled.div`
  padding-left: 36px;
  padding-top: 20px;
  padding-bottom: 15px;
  color: ${Color.BLACK_85};
  font-weight: bold;

  @media only screen and (max-width: ${MAX_DESKTOP_BREAKPOINT}) {
    padding-left: 17px;
  }
`;

const UserFeedbackText = styled.div`
  font-style: ${props => (props.hasGivenFeedback || props.hasLeftComments ? 'italic' : 'inherit')};
  color: ${props => (props.hasLeftComments ? SECONDARY_COLOR : 'inherit')};
`;

const StyledFeedBackButton = styled(Button)`
  margin-right: 14px;
`;

const StyledButtonContents = styled.div`
  display: flex;
  align-items: center;

  & > p {
    margin: 0;
    padding: 0;
    margin-left: 8px;
  }
`;

const UserFeedbackButtonGroup = styled.div`
  padding-top: 10px;
  width: 180px;
  display: flex;
  justify-content: space-between;
`;

const FormContainer = styled.div`
  max-width: 650px;
`;

const UserFeedbackComponent = ({ question, mixpanelMessage }) => {
  const { t } = useTranslation();
  const prospectId = useProspectId();
  const [hasGivenFeedback, gaveFeedback] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [leftComments, setLeftComments] = useState(false);
  const dispatch = useDispatch();
  const contactId = useContactId();

  const handleOnClick = (userAnswer, q, id) => {
    gaveFeedback(true);
    setAnswer(userAnswer);
    mixpanel.track(mixpanelMessage, {
      Category: q.category,
      Question: q.title,
      'Was helpful for user': userAnswer,
      prospectId: id,
    });
  };

  const submitFeedback = feedback => {
    dispatch(createFeedback(contactId, feedback, null, question.title));
    setLeftComments(true);
  };

  return (
    <UserFeedbackComponentContainer>
      {answer === UserAnswers.YES && <UserFeedbackText hasGivenFeedback={hasGivenFeedback}>{t('Thanks for the feedback!')}</UserFeedbackText>}
      {leftComments && (
        <UserFeedbackText hasLeftComments={leftComments}>{t('Thank you for your feedback and helping us make the FAQ page better!')}</UserFeedbackText>
      )}
      {!hasGivenFeedback && (
        <>
          <UserFeedbackText hasGivenFeedback={hasGivenFeedback}>{t('Was this information helpful?')}</UserFeedbackText>
          <UserFeedbackButtonGroup>
            <StyledFeedBackButton secondary onClick={() => handleOnClick(UserAnswers.YES, question, prospectId)} variant="outlined">
              <StyledButtonContents>
                <CheckIcon /> <p>{t('YES')}</p>
              </StyledButtonContents>
            </StyledFeedBackButton>
            <StyledFeedBackButton secondary onClick={() => handleOnClick(UserAnswers.NO, question, prospectId)} variant="outlined">
              <StyledButtonContents>
                <CloseIcon /> <p>{t('NO')}</p>
              </StyledButtonContents>
            </StyledFeedBackButton>
          </UserFeedbackButtonGroup>
        </>
      )}
      {answer === UserAnswers.NO && !leftComments && (
        <FormContainer>
          <Form
            onSubmit={submitFeedback}
            buttonText="Submit Feedback"
            label="We are always looking to improve our FAQ page. How could this answer be more helpful?"
          />
        </FormContainer>
      )}
    </UserFeedbackComponentContainer>
  );
};

UserFeedbackComponent.propTypes = {
  question: PropTypes.shape({}).isRequired,
  mixpanelMessage: PropTypes.string.isRequired,
};

export default UserFeedbackComponent;
